/**
 * The dock gate: one shared code between the public internet and the crew access terminal.
 *
 * The screen it draws is a courtesy — the server checks the same code on /gate, /hulls, /hull/:code
 * and on every room create and join, so removing the screen in devtools gets you a locked door with
 * no doormat. The code is remembered per browser so a crew member types it once.
 */
const LS_CODE = "spacecrew.dockCode";

interface SiteConfig { gated: boolean; devHull: boolean }

class Gate {
  /** null until /config answers: the app waits rather than flashing the wrong screen. */
  config = $state<SiteConfig | null>(null);
  code = $state(localStorage.getItem(LS_CODE) ?? "");
  unlocked = $state(false);
  busy = $state(false);
  error = $state("");

  /** Fetched once at boot; a gated site with a remembered code re-validates silently. */
  async load(): Promise<void> {
    try {
      const r = await fetch("/config");
      this.config = r.ok ? ((await r.json()) as SiteConfig) : { gated: false, devHull: true };
    } catch {
      this.config = { gated: false, devHull: true };   // offline dev: don't lock the developer out
    }
    if (!this.config.gated) { this.unlocked = true; return; }
    if (this.code) await this.submit(this.code, true);
  }

  async submit(code: string, quiet = false): Promise<boolean> {
    this.busy = true;
    if (!quiet) this.error = "";
    try {
      const r = await fetch("/gate", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code }) });
      if (r.ok) {
        this.code = code;
        localStorage.setItem(LS_CODE, code);
        this.unlocked = true;
        return true;
      }
      if (!quiet) this.error = r.status === 429 ? "THROTTLED" : "REJECTED";
      if (r.status === 403) localStorage.removeItem(LS_CODE);
      return false;
    } catch {
      if (!quiet) this.error = "OFFLINE";
      return false;
    } finally {
      this.busy = false;
    }
  }

  /** Sent with every join/create and on the hull lookups. Empty on an ungated deployment. */
  get accessCode(): string { return this.config?.gated ? this.code : ""; }
  get headers(): Record<string, string> { return this.accessCode ? { "x-access-code": this.accessCode } : {}; }
}

export const gate = new Gate();
